import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import ContentLoader, { Rect } from 'react-content-loader/native';
import { useTheme } from '@/context/ThemeContext';

// Chat-shaped skeleton shown while a (long) conversation positions itself
// after opening — right-aligned user bubbles alternating with left-aligned
// assistant text blocks, anchored to the bottom like a real chat. Rendered
// over the hidden message list until layout settles (see ChatView's settle
// veil), then cross-faded out.

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 22,
    overflow: 'hidden',
  },
  userRow: {
    alignItems: 'flex-end',
  },
  assistantRow: {
    alignItems: 'flex-start',
    width: '100%',
  },
});

function UserBubbleSkeleton({ colors, width = '55%', height = 42 }) {
  if (Platform.OS === 'windows') return <View style={{ width: '100%', height, alignItems: 'flex-end' }}>
    <View style={{ width, height, borderRadius: 16, backgroundColor: colors.solid_color_tertiary }} />
  </View>;
  return (
    <ContentLoader
      speed={1.4}
      width="100%"
      height={height}
      backgroundColor={colors.solid_color_tertiary}
      foregroundColor={colors.border_color_secondary}
    >
      <Rect x={`${100 - parseFloat(width)}%`} y="0" rx="16" ry="16" width={width} height={height} />
    </ContentLoader>
  );
}

function AssistantBlockSkeleton({ colors, lineWidths }) {
  const lineHeight = 13;
  const lineGap = 9;
  const height = lineWidths.length * (lineHeight + lineGap) - lineGap;
  if (Platform.OS === 'windows') return <View style={{ width: '100%', height, gap: lineGap }}>
    {lineWidths.map((width, index) => <View key={index} style={{ width, height: lineHeight, borderRadius: 4, backgroundColor: colors.solid_color_tertiary }} />)}
  </View>;
  return (
    <ContentLoader
      speed={1.4}
      width="100%"
      height={height}
      backgroundColor={colors.solid_color_tertiary}
      foregroundColor={colors.border_color_secondary}
    >
      {lineWidths.map((w, i) => (
        <Rect
          key={i}
          x="0"
          y={i * (lineHeight + lineGap)}
          rx="4"
          ry="4"
          width={w}
          height={lineHeight}
        />
      ))}
    </ContentLoader>
  );
}

export default function ChatMessagesSkeleton() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  return (
    <View style={styles.container} pointerEvents="none">
      <View style={styles.userRow}>
        <UserBubbleSkeleton colors={colors} width="48%" height={40} />
      </View>
      <View style={styles.assistantRow}>
        <AssistantBlockSkeleton colors={colors} lineWidths={['96%', '88%', '93%', '60%']} />
      </View>
      <View style={styles.userRow}>
        <UserBubbleSkeleton colors={colors} width="34%" height={40} />
      </View>
      <View style={styles.assistantRow}>
        <AssistantBlockSkeleton colors={colors} lineWidths={['92%', '97%', '85%', '90%', '42%']} />
      </View>
      <View style={styles.userRow}>
        <UserBubbleSkeleton colors={colors} width="58%" height={40} />
      </View>
      <View style={styles.assistantRow}>
        <AssistantBlockSkeleton colors={colors} lineWidths={['95%', '78%']} />
      </View>
    </View>
  );
}
